import { supabase } from '../supabase';
import { getChapterCache, mergeChapterCache } from './cache';
import { parseStoredHeadings } from './storedText';

// Only fetches columns not already in cache; merges result back into cache.
export async function fetchChapterData(bookAbbr, chapter, cols) {
  const cached  = getChapterCache(bookAbbr, chapter);
  const colArr  = cols.split(',');
  if (cached && colArr.every(c => cached[c] !== undefined)) return cached; // full cache hit
  const toFetch = cached ? colArr.filter(c => cached[c] === undefined) : colArr;
  const { data, error } = await supabase
    .from('bible_chapters')
    .select(toFetch.join(','))
    .eq('book_abbr', bookAbbr)
    .eq('chapter', chapter)
    .single();
  if (error) throw error;
  return mergeChapterCache(bookAbbr, chapter, data);
}

// Cross-references and footnotes exist for these languages only; Spanish has
// none, so callers fall back to plain text for it.
export function refsLangFor(lang) {
  return (lang === 'en' || lang === 'zh' || lang === 'sc') ? lang : null;
}

// Refs rows are kept in memory rather than sessionStorage: they are only wanted
// while the Refs view is on, and holding them for the visit is enough to make
// toggling it, or flipping back to an earlier day, free.
const _refsCache = new Map();

// Everything needed to draw one chapter with its markers in one language:
//   map      { "verse_marker": { type, content } }  from bible_refs
//   texts    { verse: markedString }                 from text_<lang>_marked
//   headings { verse: [heading, ...] }               Psalms only, from text_<lang>
// The headings let the Reading page tell a Psalm superscription from the
// second line of a poetic verse; no other book has any, so the plain text is
// not fetched for them.
export async function fetchChapterRefs(bookAbbr, chapter, lang) {
  const key = `${bookAbbr}_${chapter}_${lang}`;
  if (_refsCache.has(key)) return _refsCache.get(key);
  const markedCol = `text_${lang}_marked`;
  const cols = bookAbbr === 'Ps' ? `${markedCol},text_${lang}` : markedCol;
  const [{ data: refs, error }, ch] = await Promise.all([
    supabase.from('bible_refs').select('verse,marker,type,content')
      .eq('book_abbr', bookAbbr).eq('chapter', chapter).eq('lang', lang),
    fetchChapterData(bookAbbr, chapter, cols),
  ]);
  if (error) throw error;
  const map = {};
  (refs || []).forEach(r => { map[`${r.verse}_${r.marker}`] = { type: r.type, content: r.content }; });
  const texts = {};
  (ch?.[markedCol] || '').split('\n').forEach(line => {
    const mm = line.match(/^(\d+)\s+([\s\S]*)/);
    if (mm) texts[parseInt(mm[1], 10)] = mm[2];
  });
  const result = { map, texts, headings: bookAbbr === 'Ps' ? parseStoredHeadings(ch?.[`text_${lang}`]) : {} };
  if (_refsCache.size >= 60) _refsCache.clear(); // prevent unbounded growth
  _refsCache.set(key, result);
  return result;
}
